import * as React from 'react';

import RichText from 'src/components/common/richText/RichText';
import Notiflix from 'notiflix';
import LoaderModal from 'src/components/common/modals/LoaderModal';

import { DefaultButton, Label, PrimaryButton, TextField } from '@fluentui/react';
import { createInvoice, downloadInvoice, IInvoicePostDto } from 'src/api/invoice';
import { IInvoiceDto, InvoiceType, IProjectDto } from 'src/common/dto';
import { downloadFile, setTextAboveAndBelow } from 'src/common/functions';
import { AxiosError } from 'axios';
import { MAX_PERCENTAGE } from 'src/common/constants';

interface PartialInvoiceFormProps {
    project: IProjectDto;
    closeModal: () => void;
    setProject: (project: IProjectDto) => void;
    lastPaymentDate: Date;
}

interface PartialInvoiceFormState {
    invoice: IInvoicePostDto;
    loadingModalOpen: boolean;
}

class PartialInvoiceForm extends React.Component<PartialInvoiceFormProps, PartialInvoiceFormState> {
    constructor(props: PartialInvoiceFormProps) {
        super(props);
        this.state = {
            invoice: {
                modules: [],
                projectId: this.props.project.id,
                textAbove: '',
                textBelow: '',
                percentage: 0,
                type: InvoiceType[InvoiceType.Partial],
                textInTable: '',
                lastPaymentDate: this.props.lastPaymentDate
            },
            loadingModalOpen: false
        };

    }

    componentDidMount(): void {
        const invoice = this.state.invoice;
        setTextAboveAndBelow(this.props.project, invoice, undefined)
        this.setState({ invoice })
    }

    componentDidUpdate(prevProps: Readonly<PartialInvoiceFormProps>, prevState: Readonly<PartialInvoiceFormState>): void {
        if (prevProps.lastPaymentDate !== this.props.lastPaymentDate) {
            const invoice = this.state.invoice;
            invoice.lastPaymentDate = this.props.lastPaymentDate;

            this.setState({ invoice })
        }
    }

    changeTextInTable = (textInTable: string): void => {
        const invoice = this.state.invoice;
        invoice.textInTable = textInTable;
        this.setState({ invoice });
    }

    changeTextAbove = (textAbove: string): void => {
        const invoice = this.state.invoice;
        invoice.textAbove = textAbove;
        this.setState({ invoice });
    }

    changeTextBelow = (textBelow: string): void => {
        const invoice = this.state.invoice;
        invoice.textBelow = textBelow;
        this.setState({ invoice });
    }

    changePercentage = (event: React.SyntheticEvent<HTMLElement, Event>, newValue: string | undefined): void => {
        if (newValue !== undefined && Number(newValue) <= MAX_PERCENTAGE && Number(newValue) >= 0) {
            const percentage = Number(Number(newValue).toFixed(0));
            const invoice = this.state.invoice;
            invoice.percentage = percentage;
            this.setState({ invoice })
        }
    }

    onSaveClick = (): void => {
        this.setState({ loadingModalOpen: true })
        const project = this.props.project;
        const response = createInvoice(this.state.invoice);

        response.then(r => {
            if (r.status === 201) {
                const invoice = r.data as IInvoiceDto;

                const downloadResponse = downloadInvoice(invoice.id);

                downloadResponse.then(r => {
                    if (r.status === 200) {
                        downloadFile(r);

                        project.invoices.push(invoice);
                        project.defaultInvoiceTextAbove = this.state.invoice.textAbove;
                        project.defaultInvoiceTextBelow = this.state.invoice.textBelow;
                        project.customerDefaultInvoiceTextAbove = this.state.invoice.textAbove;
                        project.customerDefaultInvoiceTextBelow = this.state.invoice.textBelow;
                        Notiflix.Notify.success('Invoice successfully created');
                        this.setState({ loadingModalOpen: false })
                        this.props.setProject(project);
                        this.props.closeModal();
                    }
                })
                downloadResponse.catch((error: AxiosError) => {
                    Notiflix.Notify.failure(`${error.response?.data}`)
                    this.props.closeModal();
                })
            }
        })
        response.catch((error: AxiosError) => {
            Notiflix.Notify.failure(`${error.response?.data}`)
            this.props.closeModal();
        })
    }

    render(): React.ReactElement<PartialInvoiceForm> {
        return (
            <div className='w-1000 flex flex-col gap-4'>
                <TextField
                    label='Percentage'
                    suffix='%'
                    className='w-36'
                    value={this.state.invoice.percentage?.toString()}
                    onChange={this.changePercentage}
                />
                <div>
                    <Label>Text in table</Label>
                    <RichText
                        content={this.state.invoice.textInTable || ''}
                        setContent={this.changeTextInTable}
                    />
                </div>
                <div>
                    <Label>Text above</Label>
                    <RichText
                        content={this.state.invoice.textAbove}
                        setContent={this.changeTextAbove}
                    />
                </div>
                <div>
                    <Label>Text below</Label>
                    <RichText
                        content={this.state.invoice.textBelow}
                        setContent={this.changeTextBelow}
                    />
                </div>
                <div className='flex gap-5'>
                    <PrimaryButton
                        text='Create'
                        className={this.state.invoice.percentage === 0 ? 'disabled-button' : 'primary-button'}
                        disabled={this.state.invoice.percentage === 0}
                        onClick={this.onSaveClick}
                    />
                    <DefaultButton
                        text='Cancel'
                        onClick={this.props.closeModal}
                    />
                </div>

                <LoaderModal isOpen={this.state.loadingModalOpen} label='Document is creating, please wait' />
            </div>
        );
    }
}

export default PartialInvoiceForm;